import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { translations, artworks as artworksData } from "../data/mock";
import {
  ArrowLeft,
  Calendar,
  Ruler,
  Layers,
  MapPin,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import "../styles/portfolio.css";

const WorkDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();

  const norm = (s = "") =>
    String(s)
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase();

  const isFake = (w) => {
    if (!w) return false;

    // 1) series
    const seriesOk =
      w?.series && (w.series.en === "F.A.K.E." || w.series.it === "F.A.K.E.");

    // 2) category/categories
    const cats = Array.isArray(w?.categories)
      ? w.categories
      : [w?.category].filter(Boolean);

    const catOk = cats.map(norm).includes("fake");

    return seriesOk || catOk;
  };

  const isTinder = (w) => {
    if (!w) return false;

    // 1) series
    const seriesOk =
      w?.series && (w.series.en === "TIN-DER" || w.series.it === "TIN-DER");

    // 2) category/categories
    const cats = Array.isArray(w?.categories)
      ? w.categories
      : [w?.category].filter(Boolean);

    const catOk = cats.map(norm).includes("tinder");

    return seriesOk || catOk;
  };

  // Ordine robusto per le FAKE (se vuoi mantenerle in ordine “episodi”)
  const getSortKey = (w) => {
    if (w?.date) {
      const ts = Date.parse(w.date);
      if (!Number.isNaN(ts)) return ts;
    }
    if (typeof w?.episode === "number") return w.episode;
    if (typeof w?.year === "number") return w.year;
    const idNum = Number(w?.id);
    return Number.isNaN(idNum) ? 0 : idNum;
  };

  // trova opera corrente
  const work = useMemo(() => {
    return (artworksData || []).find((w) => String(w.id) === String(id)) || null;
  }, [id]);

  const workImages = useMemo(() => {
    if (!work) return [];
    return Array.isArray(work.images) && work.images.length > 0
      ? work.images
      : [work.image];
  }, [work]);

  const [imageIndex, setImageIndex] = useState(0);

  useEffect(() => {
    setImageIndex(0);
  }, [id]);

  const changeImage = (direction) => {
    if (workImages.length < 2) return;
    setImageIndex((current) =>
      (current + direction + workImages.length) % workImages.length
    );
  };

  // prepara liste separate
  const fakeList = useMemo(() => {
    return (artworksData || [])
      .filter(isFake)
      .sort((a, b) => getSortKey(b) - getSortKey(a)); // più recente -> più vecchia
  }, []);

  const tinderList = useMemo(() => {
    // ordine Progetto 1 -> 2 -> 3 -> 4 (per id crescente)
    return (artworksData || [])
      .filter(isTinder)
      .sort((a, b) => Number(a.id) - Number(b.id));
  }, []);

  const worksList = useMemo(() => {
    // manteniamo l’ordine originale del mock per le opere normali
    return (artworksData || []).filter((w) => !isFake(w) && !isTinder(w));
  }, []);

  // scegli la lista giusta in base a dove sei
  const list = isFake(work) ? fakeList : isTinder(work) ? tinderList : worksList;

  // indice corrente dentro la lista giusta
  const currentIndex = useMemo(() => {
    return list.findIndex((w) => String(w.id) === String(id));
  }, [list, id]);

  // prev/next SOLO dentro la lista giusta
  const prevWork = currentIndex > 0 ? list[currentIndex - 1] : null;
  const nextWork =
    currentIndex !== -1 && currentIndex < list.length - 1
      ? list[currentIndex + 1]
      : null;

  const goToWork = (target) => {
    if (!target) return;
    navigate(`/${language}/works/${target.id}`);
  };

  if (!work) {
    return (
      <div style={{ paddingTop: "120px", textAlign: "center" }}>
        <p className="body-text">
          {t({ it: "Opera non trovata", en: "Work not found" })}
        </p>
        <button
          onClick={() => navigate(`/${language}/works`)}
          className="btn btn-outline"
          style={{ marginTop: "var(--spacing-lg)" }}
        >
          <ArrowLeft size={16} />{" "}
          {t({ it: "Torna alle opere", en: "Back to works" })}
        </button>
      </div>
    );
  }

  // badge categoria robusto (supporta category o categories[0])
  const getCategoryLabel = () => {
    const cat =
      work.category ||
      (Array.isArray(work.categories) ? work.categories[0] : null);

    // se è fake, mostra FAKE
    if (isFake(work)) return "F.A.K.E.";

    // se è tinder, mostra TIN-DER
    if (isTinder(work)) return "TIN-DER";

    // altrimenti prova traduzione
    if (cat && translations?.works?.[cat]) return t(translations.works[cat]);

    // fallback: stampa la stringa
    return cat || "";
  };

  return (
    <div style={{ paddingTop: "100px" }} className="work-detail">
      <section className="section-spacing">
        <div className="container">
          <Link
            to={
              isFake(work)
                ? `/${language}/fake`
                : isTinder(work)
                ? `/${language}/tinder`
                : `/${language}/works`
            }
            className="btn btn-outline"
            style={{ marginBottom: "var(--spacing-lg)" }}
          >
            <ArrowLeft size={16} />{" "}
            {t({
              it: isFake(work)
                ? "Torna a F.A.K.E."
                : isTinder(work)
                ? "Torna a TIN-DER"
                : "Torna alle opere",
              en: isFake(work)
                ? "Back to F.A.K.E."
                : isTinder(work)
                ? "Back to TIN-DER"
                : "Back to works",
            })}
          </Link>

          <div className="work-grid">
            {/* Immagine */}
            <div className="image-box work-detail-image-box">
              <button
                type="button"
                className="work-detail-image-button"
                onClick={() => changeImage(1)}
                disabled={workImages.length < 2}
                aria-label={
                  workImages.length > 1
                    ? t({
                        it: "Mostra la foto successiva",
                        en: "Show the next photo",
                      })
                    : undefined
                }
              >
                <img
                  src={workImages[imageIndex]}
                  alt={`${t(work.title)}${
                    workImages.length > 1
                      ? ` — ${t({ it: "foto", en: "photo" })} ${imageIndex + 1}`
                      : ""
                  }`}
                  className="artwork-image"
                  onError={(e) => {
                    e.currentTarget.src = "/media/placeholder.jpg";
                  }}
                />
              </button>

              {workImages.length > 1 && (
                <>
                  <button
                    type="button"
                    className="artwork-carousel-arrow artwork-carousel-arrow--left"
                    onClick={() => changeImage(-1)}
                    aria-label={t({
                      it: "Foto precedente",
                      en: "Previous photo",
                    })}
                  >
                    <ChevronLeft size={24} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="artwork-carousel-arrow artwork-carousel-arrow--right"
                    onClick={() => changeImage(1)}
                    aria-label={t({
                      it: "Foto successiva",
                      en: "Next photo",
                    })}
                  >
                    <ChevronRight size={24} aria-hidden="true" />
                  </button>
                  <span className="artwork-carousel-counter" aria-live="polite">
                    {imageIndex + 1} / {workImages.length}
                  </span>
                </>
              )}
            </div>

            {/* Dettagli */}
            <div className="work-details">
              <span
                className="category-badge"
                style={{
                  marginBottom: "var(--spacing-md)",
                  display: "inline-block",
                }}
              >
                {getCategoryLabel()}
              </span>

              <h1
                className="section-title"
                style={{ marginBottom: "var(--spacing-lg)" }}
              >
                {t(work.title)}
              </h1>

              {/* Metadati */}
              <div style={{ marginBottom: "var(--spacing-xl)" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--spacing-sm)",
                    marginBottom: "var(--spacing-md)",
                  }}
                >
                  <Calendar size={18} style={{ color: "var(--color-gray-500)" }} />
                  <span className="small-text">
                    <strong>{t(translations.works.year)}:</strong> {work.year}
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--spacing-sm)",
                    marginBottom: "var(--spacing-md)",
                  }}
                >
                  <Layers size={18} style={{ color: "var(--color-gray-500)" }} />
                  <span className="small-text">
                    <strong>{t(translations.works.technique)}:</strong>{" "}
                    {work.technique ? t(work.technique) : "—"}
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--spacing-sm)",
                    marginBottom: "var(--spacing-md)",
                  }}
                >
                  <Ruler size={18} style={{ color: "var(--color-gray-500)" }} />
                  <span className="small-text">
                    <strong>{t(translations.works.dimensions)}:</strong>{" "}
                    {work.dimensions
                      ? typeof work.dimensions === "object"
                        ? t(work.dimensions)
                        : work.dimensions
                      : "—"}
                  </span>
                </div>

                {work.series && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--spacing-sm)",
                      marginBottom: "var(--spacing-md)",
                    }}
                  >
                    <MapPin size={18} style={{ color: "var(--color-gray-500)" }} />
                    <span className="small-text">
                      <strong>{t(translations.works.series)}:</strong>{" "}
                      {t(work.series)}
                    </span>
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--spacing-sm)",
                    marginTop: "var(--spacing-lg)",
                  }}
                >
                  {work.available === true ? (
                    <>
                      <CheckCircle size={18} style={{ color: "#10b981" }} />
                      <span className="small-text">{t(translations.works.available)}</span>
                    </>
                  ) : work.available === false ? (
                    <>
                      <XCircle size={18} style={{ color: "var(--color-gray-400)" }} />
                      <span className="small-text">{t(translations.works.sold)}</span>
                    </>
                  ) : null}
                </div>
              </div>

              {/* Descrizione */}
              <p
                className="body-text"
                style={{
                  fontSize: "1.1rem",
                  lineHeight: "1.8",
                  marginBottom: "var(--spacing-xl)",
                }}
              >
                {work.description ? t(work.description) : ""}
              </p>

              <Link
                to={
                  isTinder(work)
                    ? `/${language}/tinder#tinder-form`
                    : `/${language}/contact`
                }
                className="btn btn-primary"
              >
                {t({ it: "Richiedi informazioni", en: "Request information" })}
              </Link>
            </div>
          </div>

          {/* FRECCE PRECEDENTE / SUCCESSIVA (solo dentro la sezione corretta) */}
          <div className="detail-navigation">
            {prevWork && (
              <button
                type="button"
                className="nav-arrow nav-arrow-left"
                onClick={() => goToWork(prevWork)}
                aria-label={t({ it: "Opera precedente", en: "Previous work" })}
                title={t({ it: "Opera precedente", en: "Previous work" })}
              >
                <ChevronLeft size={18} className="arrow-icon" />
              </button>
            )}

            {nextWork && (
              <button
                type="button"
                className="nav-arrow nav-arrow-right"
                onClick={() => goToWork(nextWork)}
                aria-label={t({ it: "Opera successiva", en: "Next work" })}
                title={t({ it: "Opera successiva", en: "Next work" })}
              >
                <ChevronRight size={18} className="arrow-icon" />
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default WorkDetail;
